/**
 * LiveSessionPanel - Manager Control Panel for Interactive Guided Sessions
 * Allows managers to create and manage live story sessions with participants
 */

import React, { useState, useEffect, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Play,
  Pause,
  Users,
  Dice5,
  MessageSquare,
  Vote,
  Clock,
  Copy,
  Download,
  RefreshCw,
  X,
  Plus,
  Gamepad2,
  Shield,
  Key,
  UserCheck,
  UserX,
  Ban,
} from 'lucide-react';
import VersionBadge from './VersionBadge';
import StoryGraphViewer from './StoryGraphViewer';
import PlayerPreviewPanel from './PlayerPreviewPanel';
import { API_BASE_URL } from '../config/api';

const API_URL = API_BASE_URL;

const LiveSessionPanel = ({ 
  isOpen, 
  onClose, 
  storyId, 
  storyTitle, 
  currentNodeId, 
  currentNodeTitle, 
  currentNodeContent,
  tweeSource,
  allPassages = [],
  onExport 
}) => {
  // Session state
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Selected passage state (GM can select which passage to push)
  const [selectedPassageId, setSelectedPassageId] = useState(currentNodeId);
  
  // Debug mode
  const [showDebug, setShowDebug] = useState(false);
  
  // Expand/collapse states for new panels
  const [storyNavExpanded, setStoryNavExpanded] = useState(true);
  const [playerPreviewExpanded, setPlayerPreviewExpanded] = useState(true);
  
  // Manager Controls state
  const [diceType, setDiceType] = useState('d20');
  const [diceExpression, setDiceExpression] = useState('1d20');
  const [rollTarget, setRollTarget] = useState('all');
  const [rollDescription, setRollDescription] = useState('');
  const [rollResults, setRollResults] = useState(null);
  
  // Voting state (legacy)
  const [voteQuestion, setVoteQuestion] = useState('');
  const [voteHelpText, setVoteHelpText] = useState('');
  const [voteResults, setVoteResults] = useState(null);
  
  // Enhanced Voting state
  const [voteMode, setVoteMode] = useState('choice'); // 'choice' or 'poll'
  const [voteDuration, setVoteDuration] = useState(60); // seconds
  const [activeVote, setActiveVote] = useState(null);
  const [voteCountdown, setVoteCountdown] = useState(0);
  const [pollQuestion, setPollQuestion] = useState('');
  const [pollOptions, setPollOptions] = useState(['', '']);
  const [showVoteResults, setShowVoteResults] = useState(false);
  const [detailedResults, setDetailedResults] = useState(null);
  const [showVoterNames, setShowVoterNames] = useState(true);
  
  // Visitors state
  const [visitors, setVisitors] = useState([]);
  const [selectedVisitors, setSelectedVisitors] = useState([]);
  
  // Security state
  const [pinEnabled, setPinEnabled] = useState(false);
  const [currentPin, setCurrentPin] = useState(null);
  const [requireApproval, setRequireApproval] = useState(false);
  const [pendingVisitors, setPendingVisitors] = useState([]);
  
  // Polling interval
  const [pollInterval, setPollInterval] = useState(null);

  const sessionUrl = session ? `${window.location.origin}/session/${session.session_id}` : '';
  
  // Get selected passage content
  const getSelectedPassage = () => {
    const passage = allPassages.find(p => p.id === selectedPassageId);
    return passage || { id: currentNodeId, title: currentNodeTitle, content: currentNodeContent };
  };

  // Create a new session
  const createSession = async () => {
    setLoading(true);
    setError(null);
    try {
      const passage = getSelectedPassage();
      console.log('[LiveSession] Creating session...');
      console.log('[LiveSession] API_URL:', API_URL);
      console.log('[LiveSession] Passage:', passage);
      console.log('[LiveSession] StoryId:', storyId);
      console.log('[LiveSession] StoryTitle:', storyTitle);
      
      const requestBody = {
        story_id: storyId || 'default-story',
        story_title: storyTitle || 'Untitled Story',
        current_node_id: passage.id || 'Start',
        current_node_title: passage.title || 'Start',
        current_node_content: passage.content || '',
        twee_source: tweeSource || ''
      };
      console.log('[LiveSession] Request body:', requestBody);
      
      const response = await fetch(`${API_URL}/api/live-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });
      
      console.log('[LiveSession] Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('[LiveSession] Error response:', errorText);
        throw new Error(`Failed to create session: ${response.status} - ${errorText}`);
      }
      
      const data = await response.json();
      console.log('[LiveSession] Session created:', data);
      setSession(data);
      setDiceType(data.dice_type);
      setPinEnabled(data.has_pin);
      setRequireApproval(data.require_approval);
      startPolling(data.session_id);
    } catch (err) {
      console.error('[LiveSession] Create session error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch session updates
  const fetchSession = useCallback(async (sessionId) => {
    try {
      const response = await fetch(`${API_URL}/api/live-session/${sessionId}`);
      if (response.status === 410) {
        setSession(prev => prev ? { ...prev, expired: true } : null);
        stopPolling();
        return;
      }
      if (!response.ok) throw new Error('Failed to fetch session');
      const data = await response.json();
      setSession(data);
    } catch (err) {
      console.error('Error fetching session:', err);
    }
  }, []);

  // Fetch visitors
  const fetchVisitors = useCallback(async (sessionId) => {
    try {
      const response = await fetch(`${API_URL}/api/live-session/${sessionId}/visitors`);
      if (response.ok) {
        const data = await response.json();
        setVisitors(data);
      }
    } catch (err) {
      console.error('Error fetching visitors:', err);
    }
  }, []);

  // Fetch vote results
  const fetchVoteResults = useCallback(async (sessionId) => {
    try {
      const response = await fetch(`${API_URL}/api/live-session/${sessionId}/vote`);
      if (response.ok) {
        const data = await response.json();
        setVoteResults(data);
      }
    } catch (err) {
      console.error('Error fetching votes:', err);
    }
  }, []);

  // Fetch roll results
  const fetchRollResults = useCallback(async (sessionId) => {
    try {
      const response = await fetch(`${API_URL}/api/live-session/${sessionId}/roll/results`);
      if (response.ok) {
        const data = await response.json();
        setRollResults(data);
      }
    } catch (err) {
      console.error('Error fetching rolls:', err);
    }
  }, []);

  // Fetch PIN status
  const fetchPinStatus = useCallback(async (sessionId) => {
    try {
      const response = await fetch(`${API_URL}/api/live-session/${sessionId}/pin`);
      if (response.ok) {
        const data = await response.json();
        setPinEnabled(data.has_pin);
        setCurrentPin(data.pin);
      }
    } catch (err) {
      console.error('Error fetching PIN status:', err);
    }
  }, []);

  // Fetch pending visitors
  const fetchPendingVisitors = useCallback(async (sessionId) => {
    try {
      const response = await fetch(`${API_URL}/api/live-session/${sessionId}/pending-visitors`);
      if (response.ok) {
        const data = await response.json();
        setPendingVisitors(data.pending || []);
      }
    } catch (err) {
      console.error('Error fetching pending visitors:', err);
    }
  }, []);

  // Start polling for updates
  const startPolling = useCallback((sessionId) => {
    if (pollInterval) clearInterval(pollInterval);
    
    const interval = setInterval(() => {
      fetchSession(sessionId);
      fetchVisitors(sessionId);
      fetchVoteResults(sessionId);
      fetchRollResults(sessionId);
      fetchPinStatus(sessionId);
      fetchPendingVisitors(sessionId);
    }, 3000);
    
    setPollInterval(interval);
  }, [pollInterval, fetchSession, fetchVisitors, fetchVoteResults, fetchRollResults, fetchPinStatus, fetchPendingVisitors]);

  // Stop polling
  const stopPolling = useCallback(() => {
    if (pollInterval) {
      clearInterval(pollInterval);
      setPollInterval(null);
    }
  }, [pollInterval]);

  // Toggle pause/play
  const togglePause = async () => {
    if (!session) return;
    try {
      const response = await fetch(`${API_URL}/api/live-session/${session.session_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_paused: !session.is_paused })
      });
      if (response.ok) {
        const data = await response.json();
        setSession(data);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  // Update dice type
  const updateDiceType = async (newType) => {
    if (!session) return;
    setDiceType(newType);
    try {
      await fetch(`${API_URL}/api/live-session/${session.session_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dice_type: newType })
      });
    } catch (err) {
      console.error('Error updating dice type:', err);
    }
  };

  // Update history visibility
  const updateVisibility = async (visibility) => {
    if (!session) return;
    try {
      const response = await fetch(`${API_URL}/api/live-session/${session.session_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ history_visibility: visibility })
      });
      if (response.ok) {
        const data = await response.json();
        setSession(data);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  // Update to current node (can accept a passage directly or use selected)
  const updateToCurrentNode = async (passageOverride = null) => {
    if (!session) return;
    try {
      const passage = passageOverride || getSelectedPassage();
      console.log('[LiveSession] Updating to passage:', passage);
      
      const response = await fetch(`${API_URL}/api/live-session/${session.session_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          current_node_id: passage.id,
          current_node_title: passage.title,
          current_node_content: passage.content
        })
      });
      if (response.ok) {
        const data = await response.json();
        console.log('[LiveSession] Session updated:', data);
        setSession(data);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  // Extend session
  const extendSession = async () => {
    if (!session) return;
    try {
      const response = await fetch(`${API_URL}/api/live-session/${session.session_id}/extend`, {
        method: 'POST'
      });
      if (response.ok) {
        fetchSession(session.session_id);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  // Toggle PIN protection
  const togglePin = async () => {
    if (!session) return;
    try {
      const response = await fetch(`${API_URL}/api/live-session/${session.session_id}/pin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enable: !pinEnabled })
      });
      if (response.ok) {
        const data = await response.json();
        setPinEnabled(!pinEnabled);
        setCurrentPin(data.pin || null);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  // Regenerate PIN
  const regeneratePin = async () => {
    if (!session) return;
    try {
      const response = await fetch(`${API_URL}/api/live-session/${session.session_id}/pin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enable: true })
      });
      if (response.ok) {
        const data = await response.json();
        setCurrentPin(data.pin);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  // Toggle require approval
  const toggleRequireApproval = async () => {
    if (!session) return;
    try {
      const response = await fetch(`${API_URL}/api/live-session/${session.session_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ require_approval: !requireApproval })
      });
      if (response.ok) {
        setRequireApproval(!requireApproval);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  // Approve or reject a visitor
  const handleApproveVisitor = async (visitorToken, approve) => {
    if (!session) return;
    try {
      await fetch(`${API_URL}/api/live-session/${session.session_id}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ visitor_token: visitorToken, approve })
      });
      fetchPendingVisitors(session.session_id);
      fetchVisitors(session.session_id);
    } catch (err) {
      setError(err.message);
    }
  };

  // Kick a visitor
  const handleKickVisitor = async (visitorToken) => {
    if (!session) return;
    try {
      await fetch(`${API_URL}/api/live-session/${session.session_id}/kick`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ visitor_token: visitorToken })
      });
      fetchVisitors(session.session_id);
    } catch (err) {
      setError(err.message);
    }
  };

  // End session
  const endSession = async () => {
    if (!session) return;
    try {
      await fetch(`${API_URL}/api/live-session/${session.session_id}/end`, {
        method: 'POST'
      });
      stopPolling();
      setSession(prev => prev ? { ...prev, expired: true } : null);
    } catch (err) {
      setError(err.message);
    }
  };

  // Initiate dice roll
  const initiateRoll = async () => {
    if (!session) return;
    try {
      const targetTokens = rollTarget === 'selected' ? selectedVisitors : 
                          rollTarget === 'one' && selectedVisitors.length > 0 ? [selectedVisitors[0]] : [];
      
      await fetch(`${API_URL}/api/live-session/${session.session_id}/roll/initiate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          dice_expression: diceExpression,
          target: rollTarget,
          target_tokens: targetTokens,
          description: rollDescription
        })
      });
      fetchRollResults(session.session_id);
    } catch (err) {
      setError(err.message);
    }
  };

  // Clear roll
  const clearRoll = async () => {
    if (!session) return;
    try {
      await fetch(`${API_URL}/api/live-session/${session.session_id}/roll/clear`, {
        method: 'POST'
      });
      setRollResults(null);
    } catch (err) {
      setError(err.message);
    }
  };

  // Post vote question
  const postVoteQuestion = async () => {
    if (!session || !voteQuestion.trim()) return;
    try {
      await fetch(`${API_URL}/api/live-session/${session.session_id}/vote/question`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question_text: voteQuestion,
          help_text: voteHelpText
        })
      });
      fetchVoteResults(session.session_id);
    } catch (err) {
      setError(err.message);
    }
  };

  // Clear vote
  const clearVote = async () => {
    if (!session) return;
    try {
      await fetch(`${API_URL}/api/live-session/${session.session_id}/vote/clear`, {
        method: 'POST'
      });
      setVoteResults(null);
      setVoteQuestion('');
      setVoteHelpText('');
    } catch (err) {
      setError(err.message);
    }
  };

  // ============== Enhanced Voting Functions ==============
  
  // Start a choice vote (on navigation choices)
  const startChoiceVote = async () => {
    if (!session) return;
    
    // Get choices from current node content
    const choices = extractChoicesFromContent(currentNodeContent || '');
    if (choices.length === 0) {
      setError('No navigation choices found in current node');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/live-session/${session.session_id}/choice-vote/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          duration_seconds: voteDuration,
          choices: choices
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setActiveVote(data);
        setVoteCountdown(voteDuration);
        startCountdownTimer();
      }
    } catch (err) {
      setError(err.message);
    }
  };
  
  // Start a poll (ad-hoc question)
  const startPoll = async () => {
    if (!session || !pollQuestion.trim()) return;
    
    const validOptions = pollOptions.filter(o => o.trim());
    
    try {
      const response = await fetch(`${API_URL}/api/live-session/${session.session_id}/poll/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question: pollQuestion,
          duration_seconds: voteDuration,
          options: validOptions.length > 0 ? validOptions : null
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setActiveVote(data);
        setVoteCountdown(voteDuration);
        startCountdownTimer();
      }
    } catch (err) {
      setError(err.message);
    }
  };
  
  // Start countdown timer
  const startCountdownTimer = () => {
    const interval = setInterval(() => {
      setVoteCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          fetchVoteResults2();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };
  
  // Fetch enhanced vote state
  const fetchVoteState = async () => {
    if (!session) return;
    try {
      const response = await fetch(`${API_URL}/api/live-session/${session.session_id}/vote/state`);
      if (response.ok) {
        const data = await response.json();
        setActiveVote(data);
        if (data.seconds_remaining) {
          setVoteCountdown(data.seconds_remaining);
        }
      }
    } catch (err) {
      console.error('Failed to fetch vote state:', err);
    }
  };
  
  // Fetch detailed results
  const fetchVoteResults2 = async () => {
    if (!session) return;
    try {
      const response = await fetch(`${API_URL}/api/live-session/${session.session_id}/vote/results?show_names=${showVoterNames}`);
      if (response.ok) {
        const data = await response.json();
        setDetailedResults(data);
        setShowVoteResults(true);
      }
    } catch (err) {
      console.error('Failed to fetch vote results:', err);
    }
  };
  
  // End vote early
  const endVoteEarly = async () => {
    if (!session) return;
    try {
      const response = await fetch(`${API_URL}/api/live-session/${session.session_id}/vote/end`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          share_results: true,
          show_voter_names: showVoterNames
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setDetailedResults({
          ...data,
          total_votes: data.total_votes,
          results: data.results,
          top_three: data.results.slice(0, 3).map(r => r.value)
        });
        setVoteCountdown(0);
        setShowVoteResults(true);
      }
    } catch (err) {
      setError(err.message);
    }
  };
  
  // Share results with participants
  const shareResults = async () => {
    if (!session) return;
    try {
      await fetch(`${API_URL}/api/live-session/${session.session_id}/vote/share`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          show_voter_names: showVoterNames,
          show_percentages: true
        })
      });
    } catch (err) {
      setError(err.message);
    }
  };
  
  // Clear active vote
  const clearActiveVote = async () => {
    if (!session) return;
    try {
      await fetch(`${API_URL}/api/live-session/${session.session_id}/vote/clear-active`, {
        method: 'POST'
      });
      setActiveVote(null);
      setDetailedResults(null);
      setShowVoteResults(false);
      setVoteCountdown(0);
      setPollQuestion('');
      setPollOptions(['', '']);
    } catch (err) {
      setError(err.message);
    }
  };
  
  // Extract choices from Twee content
  const extractChoicesFromContent = (content) => {
    const choices = [];
    const linkRegex = /\[\[([^\]]+)\]\]/g;
    let match;
    
    while ((match = linkRegex.exec(content || '')) !== null) {
      const linkContent = match[1];
      let label;
      
      if (linkContent.includes('->')) {
        label = linkContent.split('->')[0].trim();
      } else if (linkContent.includes('|')) {
        label = linkContent.split('|')[0].trim();
      } else {
        label = linkContent.trim();
      }
      
      // Remove formatting markers
      label = label.replace(/\{\{\{[^}]+\}\}\}/g, '').trim();
      
      if (label && !choices.includes(label)) {
        choices.push(label);
      }
    }
    
    return choices;
  };
  
  // Format countdown time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}:${secs.toString().padStart(2, '0')}` : `${secs}s`;
  };

  // Export with session log (enhanced with all data)
  const handleExport = async () => {
    if (!session) return;
    try {
      // First try the enhanced export
      const response = await fetch(`${API_URL}/api/live-session/${session.session_id}/export`);
      
      if (response.ok) {
        const data = await response.json();
        
        // Create formatted export with timestamp
        const now = new Date();
        const timestamp = now.toISOString().slice(0, 16).replace('T', '_').replace(':', '-');
        const filename = `${storyTitle || 'Story'}_Session_${timestamp}.json`;
        
        // Trigger JSON download
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        // Also try the old export for Twee file
        const oldResponse = await fetch(`${API_URL}/api/live-session/${session.session_id}/export`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ twee_source: tweeSource })
        });
        
        if (oldResponse.ok) {
          const oldData = await oldResponse.json();
          if (oldData.twee_content) {
            const tweeBlob = new Blob([oldData.twee_content], { type: 'text/plain' });
            const tweeUrl = URL.createObjectURL(tweeBlob);
            const tweeA = document.createElement('a');
            tweeA.href = tweeUrl;
            tweeA.download = `${storyTitle || 'Story'}_Session_${timestamp}.twee`;
            document.body.appendChild(tweeA);
            tweeA.click();
            document.body.removeChild(tweeA);
            URL.revokeObjectURL(tweeUrl);
          }
        }
        
        fetchSession(session.session_id);
        
        if (onExport) onExport(data.twee_content);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  // Copy URL to clipboard
  const copyUrl = () => {
    navigator.clipboard.writeText(sessionUrl);
  };

  // Calculate time remaining
  const getTimeRemaining = () => {
    if (!session) return '00:00';
    const now = new Date();
    const expires = new Date(session.expires_at);
    const diff = Math.max(0, expires - now);
    const minutes = Math.floor(diff / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  // Update time display
  const [timeRemaining, setTimeRemaining] = useState('00:00');
  useEffect(() => {
    if (!session) return;
    const timer = setInterval(() => {
      setTimeRemaining(getTimeRemaining());
    }, 1000);
    return () => clearInterval(timer);
  }, [session]);

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <Gamepad2 className="h-5 w-5 text-purple-600" />
              Interactive Guided Sessions
              <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded">Beta</span>
              <span className="ml-1 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded">Pro</span>
            </DialogTitle>
            <VersionBadge page="gmPanel" />
          </div>
          <DialogDescription>
            Guide participants through interactive story experiences in real-time
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-md text-sm">
            {error}
          </div>
        )}

        {!session ? (
          // Create Session View
          <div className="space-y-6 py-4">
            {/* Feature Explanation */}
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-5">
              <h3 className="font-semibold text-purple-900 mb-2 flex items-center gap-2">
                <Gamepad2 className="h-5 w-5" />
                What are Interactive Guided Sessions?
              </h3>
              <p className="text-sm text-purple-800 mb-4">
                Host real-time interactive experiences where you guide participants through branching narratives, 
                decision points, and engaging content. Participants join via a simple link or QR code and see 
                content as you navigate through your story.
              </p>
              
              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <div className="bg-white/70 rounded-lg p-3 border border-purple-100">
                  <h4 className="font-medium text-purple-900 text-sm mb-1">📊 Business Consultants</h4>
                  <p className="text-xs text-purple-700">
                    Guide clients through decision trees, process flows, or strategic options. 
                    Present &ldquo;what-if&rdquo; scenarios and let stakeholders vote on preferred paths.
                  </p>
                </div>
                <div className="bg-white/70 rounded-lg p-3 border border-purple-100">
                  <h4 className="font-medium text-purple-900 text-sm mb-1">🎓 Teachers & Trainers</h4>
                  <p className="text-xs text-purple-700">
                    Create interactive lessons where students make choices that affect outcomes. 
                    Perfect for case studies, ethics discussions, or choose-your-own-adventure learning.
                  </p>
                </div>
              </div>
              
              <div className="mt-4 pt-3 border-t border-purple-200">
                <p className="text-xs text-purple-600 flex items-center gap-1">
                  <span className="inline-block w-2 h-2 bg-green-500 rounded-full"></span>
                  <strong>Early Access:</strong> This Pro feature is temporarily unlocked for all early customers.
                </p>
              </div>
            </div>

            <div className="text-center">
              <p className="text-slate-600 mb-4">
                Start a live session for &ldquo;{storyTitle || 'your story'}&rdquo;. Participants will be able to join
                and follow along as you guide them through the narrative.
              </p>
              <Button 
                onClick={createSession} 
                disabled={loading}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {loading ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Play className="h-4 w-4 mr-2" />
                )}
                Start Live Session
              </Button>
            </div>
          </div>
        ) : (
          // Active Session View
          <div className="space-y-4">
            {/* Session Status Bar */}
            <div className="flex items-center justify-between bg-slate-100 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <span className={`w-3 h-3 rounded-full ${session.is_paused ? 'bg-yellow-500' : 'bg-green-500'}`} />
                <span className="font-medium">
                  {session.expired ? 'Expired' : session.is_paused ? 'PAUSED' : 'LIVE'}
                </span>
                <span className="text-slate-500">|</span>
                <span className="text-sm text-slate-600">ID: {session.session_id}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-slate-500" />
                <span className="font-mono text-sm">{timeRemaining}</span>
              </div>
            </div>

            {/* Play/Pause Controls */}
            <div className="flex gap-2">
              <Button
                onClick={togglePause}
                disabled={session.expired}
                variant={session.is_paused ? 'default' : 'outline'}
                className={session.is_paused ? 'bg-green-600 hover:bg-green-700' : ''}
              >
                {session.is_paused ? (
                  <>
                    <Play className="h-4 w-4 mr-2" /> PLAY
                  </>
                ) : (
                  <>
                    <Pause className="h-4 w-4 mr-2" /> PAUSE
                  </>
                )}
              </Button>
              <Button onClick={extendSession} variant="outline" disabled={session.expired}>
                <Plus className="h-4 w-4 mr-1" /> 1 Hour
              </Button>
              <Button onClick={endSession} variant="destructive" disabled={session.expired}>
                End Session
              </Button>
            </div>

            {/* QR Code and URL */}
            <div className="flex gap-4 bg-white border rounded-lg p-4">
              <div className="flex-shrink-0">
                <QRCodeSVG value={sessionUrl} size={100} />
              </div>
              <div className="flex-1 space-y-2">
                <Label>Session URL</Label>
                <div className="flex gap-2">
                  <Input value={sessionUrl} readOnly className="font-mono text-sm" />
                  <Button onClick={copyUrl} variant="outline" size="icon">
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-slate-500">Share this URL or QR code with your participants</p>
              </div>
            </div>

            {/* Security Settings */}
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-indigo-600" />
                <span className="font-medium text-indigo-800">Security Settings</span>
              </div>
              
              {/* PIN Protection */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Key className="h-4 w-4 text-slate-500" />
                  <span className="text-sm">PIN Protection</span>
                </div>
                <div className="flex items-center gap-2">
                  {pinEnabled && currentPin && (
                    <span className="font-mono text-lg font-bold text-indigo-700 bg-white px-3 py-1 rounded border">
                      {currentPin}
                    </span>
                  )}
                  {pinEnabled && (
                    <Button onClick={regeneratePin} size="sm" variant="outline" className="text-xs">
                      <RefreshCw className="h-3 w-3 mr-1" /> New PIN
                    </Button>
                  )}
                  <Button 
                    onClick={togglePin} 
                    size="sm" 
                    variant={pinEnabled ? 'default' : 'outline'}
                    className={pinEnabled ? 'bg-indigo-600 hover:bg-indigo-700' : ''}
                  >
                    {pinEnabled ? 'Disable' : 'Enable'}
                  </Button>
                </div>
              </div>
              
              {/* Require Approval */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <UserCheck className="h-4 w-4 text-slate-500" />
                  <span className="text-sm">Require Manager Approval</span>
                </div>
                <Button 
                  onClick={toggleRequireApproval} 
                  size="sm" 
                  variant={requireApproval ? 'default' : 'outline'}
                  className={requireApproval ? 'bg-indigo-600 hover:bg-indigo-700' : ''}
                >
                  {requireApproval ? 'Enabled' : 'Disabled'}
                </Button>
              </div>
              
              {/* Pending Visitors */}
              {requireApproval && pendingVisitors.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded p-3 mt-2">
                  <div className="flex items-center gap-2 mb-2">
                    <UserX className="h-4 w-4 text-amber-600" />
                    <span className="text-sm font-medium text-amber-800">
                      Pending Approval ({pendingVisitors.length})
                    </span>
                  </div>
                  <div className="space-y-2">
                    {pendingVisitors.map((v) => (
                      <div key={v.visitor_token} className="flex items-center justify-between bg-white rounded px-3 py-2">
                        <span className="text-sm">{v.visitor_name}</span>
                        <div className="flex gap-1">
                          <Button 
                            onClick={() => handleApproveVisitor(v.visitor_token, true)} 
                            size="sm" 
                            className="bg-green-600 hover:bg-green-700 h-7 px-2"
                          >
                            <UserCheck className="h-3 w-3 mr-1" /> Approve
                          </Button>
                          <Button 
                            onClick={() => handleApproveVisitor(v.visitor_token, false)} 
                            size="sm" 
                            variant="destructive"
                            className="h-7 px-2"
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <p className="text-xs text-indigo-600">
                {pinEnabled ? 'Participants need the 4-digit PIN to join.' : ''}
                {pinEnabled && requireApproval ? ' ' : ''}
                {requireApproval ? 'New participants need your approval before participating.' : ''}
                {!pinEnabled && !requireApproval ? 'Session is open. Anyone with the link can join.' : ''}
              </p>
            </div>

            {/* Story Navigator - Above Participants Online */}
            <StoryGraphViewer
              passages={allPassages}
              focusNodeId={selectedPassageId}
              onSetFocus={(nodeId) => {
                setSelectedPassageId(nodeId);
                // Auto-push to session if active
                if (session && !session.is_paused) {
                  const passage = allPassages.find(p => p.id === nodeId);
                  if (passage) {
                    updateToCurrentNode(passage);
                  }
                }
              }}
              isExpanded={storyNavExpanded}
              onToggleExpand={() => setStoryNavExpanded(!storyNavExpanded)}
            />

            {/* Online Participants */}
            <div className="bg-white border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Users className="h-4 w-4 text-slate-600" />
                <span className="font-medium">Participants Online ({visitors.filter(v => v.status !== 'kicked').length})</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {visitors.filter(v => v.status !== 'kicked').length === 0 ? (
                  <span className="text-sm text-slate-500">No participants yet</span>
                ) : (
                  visitors.filter(v => v.status !== 'kicked').map((v) => (
                    <div 
                      key={v.visitor_token}
                      className={`flex items-center gap-1 px-2 py-1 text-sm rounded-full ${
                        selectedVisitors.includes(v.visitor_token) 
                          ? 'bg-purple-100 text-purple-700 border border-purple-300' 
                          : 'bg-slate-100 text-slate-700'
                      }`}
                    >
                      <span 
                        className="cursor-pointer"
                        onClick={() => {
                          if (selectedVisitors.includes(v.visitor_token)) {
                            setSelectedVisitors(selectedVisitors.filter(t => t !== v.visitor_token));
                          } else {
                            setSelectedVisitors([...selectedVisitors, v.visitor_token]);
                          }
                        }}
                      >
                        {v.visitor_name}
                        {v.status === 'pending' && <span className="text-amber-500 ml-1">(pending)</span>}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleKickVisitor(v.visitor_token);
                        }}
                        className="ml-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded p-0.5"
                        title="Remove participant"
                      >
                        <Ban className="h-3 w-3" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Participant Preview - Below Participants Online */}
            <PlayerPreviewPanel
              nodeTitle={getSelectedPassage()?.title}
              nodeContent={getSelectedPassage()?.content}
              isPaused={session?.is_paused}
              isExpanded={playerPreviewExpanded}
              onToggleExpand={() => setPlayerPreviewExpanded(!playerPreviewExpanded)}
              onChoiceClick={(targetNodeId) => {
                // Navigate to the choice's target node
                setSelectedPassageId(targetNodeId);
              }}
            />

            {/* Settings Row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Dice Type</Label>
                <Select value={diceType} onValueChange={updateDiceType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="d2">d2</SelectItem>
                    <SelectItem value="d4">d4</SelectItem>
                    <SelectItem value="d6">d6</SelectItem>
                    <SelectItem value="d8">d8</SelectItem>
                    <SelectItem value="d10">d10</SelectItem>
                    <SelectItem value="d12">d12</SelectItem>
                    <SelectItem value="d20">d20</SelectItem>
                    <SelectItem value="d100">d100</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>History Visibility</Label>
                <Select 
                  value={session.history_visibility} 
                  onValueChange={updateVisibility}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NONE">Current Only</SelectItem>
                    <SelectItem value="LAST_ONLY">Last + Current</SelectItem>
                    <SelectItem value="ALL">All History</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Push to Players Button */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="font-medium text-green-800">
                  Current FOCUS: {getSelectedPassage()?.title || 'None selected'}
                </Label>
                <button 
                  onClick={() => setShowDebug(!showDebug)}
                  className="text-xs text-green-600 hover:text-green-800"
                >
                  {showDebug ? 'Hide Debug' : 'Debug'}
                </button>
              </div>
              
              <Button 
                onClick={() => updateToCurrentNode()} 
                className="w-full bg-green-600 hover:bg-green-700"
                disabled={!selectedPassageId}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Push FOCUS to Participants Now
              </Button>
              
              <p className="text-xs text-green-700">
                Select a node in Story Navigator above, then push it to participants
              </p>
              
              {/* Debug Panel */}
              {showDebug && (
                <div className="bg-slate-100 rounded p-2 text-xs font-mono space-y-1">
                  <p><strong>Debug Info:</strong></p>
                  <p>Story ID: {storyId}</p>
                  <p>Session ID: {session?.session_id}</p>
                  <p>Current Node: {session?.current_node_id}</p>
                  <p>Content Length: {session?.current_node_content?.length || 0} chars</p>
                  <p>Passages Available: {allPassages.length}</p>
                  <p>Selected: {selectedPassageId}</p>
                  <p>Is Paused: {session?.is_paused ? 'Yes' : 'No'}</p>
                </div>
              )}
            </div>

            {/* Dice Roll Section */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Dice5 className="h-4 w-4 text-amber-600" />
                <span className="font-medium text-amber-800">Dice Roll</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Expression</Label>
                  <Input 
                    value={diceExpression} 
                    onChange={(e) => setDiceExpression(e.target.value)}
                    placeholder="2d6 + 1d4"
                    className="text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs">Target</Label>
                  <Select value={rollTarget} onValueChange={setRollTarget}>
                    <SelectTrigger className="text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Participants</SelectItem>
                      <SelectItem value="selected">Selected Participants</SelectItem>
                      <SelectItem value="one">One Participant</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Input 
                value={rollDescription}
                onChange={(e) => setRollDescription(e.target.value)}
                placeholder="Description (optional)"
                className="text-sm"
              />
              <div className="flex gap-2">
                <Button onClick={initiateRoll} size="sm" className="bg-amber-600 hover:bg-amber-700">
                  🎲 Roll Dice
                </Button>
                {rollResults?.active && (
                  <Button onClick={clearRoll} size="sm" variant="outline">
                    Clear Roll
                  </Button>
                )}
              </div>
              {rollResults?.active && rollResults.results?.length > 0 && (
                <div className="mt-2 text-sm bg-white rounded p-2">
                  <p className="font-medium">Results for {rollResults.dice_expression}:</p>
                  {rollResults.results.map((r, i) => (
                    <p key={i} className="text-slate-600">
                      {r.visitor_name}: {r.values.join(', ')} = {r.sum}
                    </p>
                  ))}
                </div>
              )}
            </div>

            {/* Enhanced Voting Section */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Vote className="h-4 w-4 text-blue-600" />
                  <span className="font-medium text-blue-800">Voting</span>
                </div>
                {activeVote?.is_active && (
                  <div className="flex items-center gap-2 text-red-600">
                    <Clock className="h-4 w-4 animate-pulse" />
                    <span className="font-mono font-bold">{formatTime(voteCountdown)}</span>
                  </div>
                )}
              </div>
              
              {/* Active Vote Display */}
              {activeVote?.is_active && (
                <div className="bg-white border border-blue-300 rounded-lg p-3 space-y-3">
                  <div className="text-sm">
                    <span className="font-medium">
                      {activeVote.vote_type === 'choice' ? '🗳️ Choice Vote Active' : '📊 Poll Active'}
                    </span>
                    {activeVote.question && (
                      <p className="text-slate-600 mt-1">{activeVote.question}</p>
                    )}
                  </div>
                  
                  {activeVote.choices && (
                    <div className="flex flex-wrap gap-2">
                      {activeVote.choices.map((choice, i) => (
                        <span key={i} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm">
                          {choice}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">
                      Votes: <strong>{activeVote.total_votes || 0}</strong>
                    </span>
                    <div className="flex gap-2">
                      <Button onClick={fetchVoteResults2} size="sm" variant="outline">
                        View Results
                      </Button>
                      <Button onClick={endVoteEarly} size="sm" className="bg-red-600 hover:bg-red-700">
                        End Now
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Vote Results Modal */}
              {showVoteResults && detailedResults && (
                <div className="bg-white border border-green-300 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-green-800">📊 Vote Results</span>
                    <Button 
                      onClick={() => setShowVoteResults(false)} 
                      size="sm" 
                      variant="ghost"
                      className="h-6 w-6 p-0"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  {detailedResults.question && (
                    <p className="text-sm text-slate-700 italic">{detailedResults.question}</p>
                  )}
                  
                  <div className="text-sm text-slate-600">
                    Total votes: <strong>{detailedResults.total_votes}</strong>
                  </div>
                  
                  {/* Top 3 */}
                  {detailedResults.top_three?.length > 0 && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded p-2">
                      <span className="text-xs font-medium text-yellow-800">🏆 Top Results:</span>
                      <div className="flex gap-2 mt-1">
                        {detailedResults.top_three.map((item, i) => (
                          <span key={i} className={`px-2 py-1 rounded text-xs font-medium ${
                            i === 0 ? 'bg-yellow-400 text-yellow-900' :
                            i === 1 ? 'bg-slate-300 text-slate-900' :
                            'bg-amber-200 text-amber-900'
                          }`}>
                            #{i + 1} {item}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* All Results */}
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {detailedResults.results?.map((result, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <div className="flex-1">
                          <div className="flex items-center justify-between text-sm">
                            <span className="font-medium">{result.value}</span>
                            <span className="text-slate-600">
                              {result.count} ({result.percentage}%)
                            </span>
                          </div>
                          <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-blue-500 rounded-full transition-all"
                              style={{ width: `${result.percentage}%` }}
                            />
                          </div>
                          {showVoterNames && result.voters?.length > 0 && (
                            <div className="text-xs text-slate-500 mt-1">
                              {result.voters.join(', ')}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Share Controls */}
                  <div className="flex items-center justify-between pt-2 border-t">
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={showVoterNames}
                        onChange={(e) => setShowVoterNames(e.target.checked)}
                        className="rounded"
                      />
                      Show voter names
                    </label>
                    <Button onClick={shareResults} size="sm" className="bg-green-600 hover:bg-green-700">
                      Share with Participants
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Create New Vote Controls (when no active vote) */}
              {(!activeVote || !activeVote.is_active) && !showVoteResults && (
                <div className="space-y-3">
                  {/* Vote Mode Selector */}
                  <div className="flex gap-2">
                    <Button
                      onClick={() => setVoteMode('choice')}
                      variant={voteMode === 'choice' ? 'default' : 'outline'}
                      size="sm"
                      className={voteMode === 'choice' ? 'bg-blue-600' : ''}
                    >
                      🗳️ Choice Vote
                    </Button>
                    <Button
                      onClick={() => setVoteMode('poll')}
                      variant={voteMode === 'poll' ? 'default' : 'outline'}
                      size="sm"
                      className={voteMode === 'poll' ? 'bg-blue-600' : ''}
                    >
                      📊 Ad-hoc Poll
                    </Button>
                  </div>
                  
                  {/* Duration Selector */}
                  <div className="flex items-center gap-2">
                    <Label className="text-xs whitespace-nowrap">Duration:</Label>
                    <Select value={voteDuration.toString()} onValueChange={(v) => setVoteDuration(parseInt(v))}>
                      <SelectTrigger className="w-32 h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="5">5 seconds</SelectItem>
                        <SelectItem value="10">10 seconds</SelectItem>
                        <SelectItem value="30">30 seconds</SelectItem>
                        <SelectItem value="60">1 minute</SelectItem>
                        <SelectItem value="120">2 minutes</SelectItem>
                        <SelectItem value="300">5 minutes</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Choice Vote */}
                  {voteMode === 'choice' && (
                    <div className="space-y-2">
                      <p className="text-xs text-slate-600">
                        Participants will vote on the navigation choices in the current passage.
                      </p>
                      {extractChoicesFromContent(currentNodeContent || '').length > 0 ? (
                        <div className="flex flex-wrap gap-1 mb-2">
                          {extractChoicesFromContent(currentNodeContent || '').map((choice, i) => (
                            <span key={i} className="px-2 py-1 bg-slate-100 text-slate-700 rounded text-xs">
                              {choice}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-amber-600">No choices found in current node.</p>
                      )}
                      <Button 
                        onClick={startChoiceVote} 
                        size="sm" 
                        className="bg-blue-600 hover:bg-blue-700"
                        disabled={extractChoicesFromContent(currentNodeContent || '').length === 0}
                      >
                        🗳️ Start Choice Vote
                      </Button>
                    </div>
                  )}
                  
                  {/* Ad-hoc Poll */}
                  {voteMode === 'poll' && (
                    <div className="space-y-2">
                      <div>
                        <Label className="text-xs">Poll Question</Label>
                        <Input 
                          value={pollQuestion}
                          onChange={(e) => setPollQuestion(e.target.value)}
                          placeholder="What do you think about...?"
                          className="text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Options (leave empty for free text)</Label>
                        {pollOptions.map((opt, i) => (
                          <div key={i} className="flex gap-1 mt-1">
                            <Input
                              value={opt}
                              onChange={(e) => {
                                const newOpts = [...pollOptions];
                                newOpts[i] = e.target.value;
                                setPollOptions(newOpts);
                              }}
                              placeholder={`Option ${i + 1}`}
                              className="text-sm"
                            />
                            {i === pollOptions.length - 1 && (
                              <Button 
                                onClick={() => setPollOptions([...pollOptions, ''])}
                                size="sm"
                                variant="ghost"
                              >
                                <Plus className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                      <Button 
                        onClick={startPoll} 
                        size="sm" 
                        className="bg-blue-600 hover:bg-blue-700"
                        disabled={!pollQuestion.trim()}
                      >
                        📊 Start Poll
                      </Button>
                    </div>
                  )}
                  
                  {/* Clear button if there's old vote data */}
                  {(detailedResults || voteResults?.vote_id) && (
                    <Button onClick={clearActiveVote} size="sm" variant="outline" className="w-full">
                      Clear Previous Vote
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Export Button */}
            <Button 
              onClick={handleExport} 
              className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
            >
              <Download className="h-4 w-4 mr-2" />
              Export Story with Session Log
            </Button>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LiveSessionPanel;
